import React, { useRef, useState, useEffect } from "react";
import {
    Alert,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    Fab,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Radio,
    RadioGroup,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import { useNavigate, useParams } from "react-router-dom";
import {Chat, Course, CoursePlan, Message} from "../types/courseTypes";
import MessageArray from "../components/MessageArray";
import {
    addMessage,
    deleteMessage,
    fetchCourseInfo,
    fetchMessage,
    fetchModes,
    fetchCourse,
    fetchCourseChats,
    fetchLLMs, updateMessage,
} from "../services/MentorApiService";
import SendIcon from "@mui/icons-material/Send";
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ChatDialog from "./ChatDialog";
import localization from '../locals/en.json'
import { useSnackbar } from './DashBoard';
import {llmList} from "./llmList";
import EvaluationRatingMessage from "./EvaluationRatingMessage";

const EvaluationPage = () => {
    const courseId = useParams<{ id: string }>().id!;
    const chatId = useParams<{ chat_id: string }>().chat_id!;
    const navigate = useNavigate();
    const [course, setCourse] = useState<Course | null>(null);
    const [coursePlan, setCoursePlan] = useState<CoursePlan | null>(null);
    const [input, setInput] = useState("");
    const [messages, setMessages] = useState<Message[]>([]);
    const [loadingMessages, setLoadingMessages] = useState<number[]>([]);
    const [initialLoading, setInitialLoading] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [openChatDialog, setOpenChatDialog] = useState(false);
    const [modes, setModes] = useState([]);
    const [llms, setLlms] = useState<{ id: number; name: string }[]>([]);
    const [selectedLlm, setSelectedLlm] = useState<number>(3);
    const [suggestionChatId, setSuggestionChatId] = useState<number>(0);
    const { showSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [selectedSailCategoryIds, setSelectedSailCategoryIds] = useState<number[]>([]);
    const EVALUATION_MODE_ID = 3;

    const tempInitialMessage: Message = {
        id: 0,
        chatId: parseInt(chatId, 10),
        inputText: "000000",
        responseText: "",
        createdAt: new Date(),
    };

    const generateTempId = () => Math.floor(Math.random() * -1000000);

    const fetchSuggestionChat = () => {
        fetchCourseChats(parseInt(courseId, 10))
            .then((response) => {
                const filteredChats = response.data.chats.filter((chat: Chat) => chat.modeId !== EVALUATION_MODE_ID);
                if(filteredChats.length === 0) return;
                setSuggestionChatId(filteredChats[0].id);

            })
            .catch((error) => {
                showSnackbar('Failed to fetch Evaluation chat', 'error');
            });
    }

    const handleFetchMessages = () => {
        fetchMessage(parseInt(courseId, 10), parseInt(chatId, 10))
            .then((response) => {
                setMessages(response.data.messages);
                setLoadingMessages([]);
            })
            .catch((error) => {
                showSnackbar('Failed to fetch messages', 'error');
            });
    };

    const handleFetchCourse = () => {
        fetchCourse(parseInt(courseId, 10))
            .then((response) => {
                setCourse(response.data);
            })
            .catch((error) => {
                showSnackbar('Failed to fetch course', 'error');
            });
    };

    const handleFetchLLMs = () => {
        fetchLLMs()
            .then((response) => {
                setLlms(response.data.llms);
            })
            .catch((error) => {
                showSnackbar('Failed to fetch LLMs', 'error');
            });
    };

    const handleCourseInfoRequest = () => {
        fetchCourseInfo(parseInt(courseId, 10))
            .then((response) => {
                setCoursePlan(response.data.courseInfos[0]);
            })
            .catch((error) => {
                showSnackbar('Failed to fetch course info', 'error');
            });
    };

    const handleDeleteMessage = (id: number) => {
        deleteMessage(parseInt(courseId, 10), parseInt(chatId, 10), id)
            .then(() => {
                handleFetchMessages();
            })
            .catch((error) => {
                showSnackbar('Failed to delete message', 'error');
            });
    };

    const handleUpdateMessage = async (messageId: number, inputText?: string): Promise<void> => {
        // Add the message ID to loadingMessages to indicate it's being updated
        setLoadingMessages((prev) => [...prev, messageId]);

        // Construct parameters based on whether inputText is provided
        const params: { llmId: number; inputText?: string } = {
            llmId: selectedLlm,
        };

        if (inputText !== undefined && inputText !== null) {
            params.inputText = inputText;
        }

        try {
            // Make API call to update the message
            await updateMessage(parseInt(courseId, 10), parseInt(chatId, 10), messageId, params);
            // Fetch updated messages
            await handleFetchMessages();
        } catch (error) {
            showSnackbar('Failed to update message', 'error');
        } finally {
            // Remove the message ID from loadingMessages regardless of success or failure
            setLoadingMessages((prev) => prev.filter((id) => id !== messageId));
        }
    };

    const handleFetchModes = () => {
        fetchModes()
            .then((response) => {
                setModes(response.data.modes);
            })
            .catch((error) => {
                showSnackbar('Failed to fetch modes', 'error');
            });
    };

    useEffect(() => {
        handleFetchCourse();
        handleCourseInfoRequest();
        handleFetchMessages();
        handleFetchModes();
        handleFetchLLMs();
        fetchSuggestionChat();
    }, []);

    useEffect(() => {
        if (messages.length === 0 && !initialLoading && course && coursePlan) {
            setInitialLoading(true);
            sendInitialMessage();
        }
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    const handleAddMessage = (params: any, tempId?: number) => {
        return addMessage(parseInt(courseId, 10), parseInt(chatId, 10), params)
            .then(() => {
                handleFetchMessages();
                setInput("");
                if (tempId) {
                    setLoadingMessages((prev) => prev.filter((id) => id !== tempId));
                }
            })
            .catch((error) => {
                showSnackbar('Failed to add message', 'error');
                if (tempId) {
                    setLoadingMessages((prev) => prev.filter((id) => id !== tempId));
                    setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
                }
                throw error;
            });
    };

    const sendInitialMessage = async () => {
        if (!coursePlan || !course) return;
        try {
            const params = {
                inputText: `Please evaluate my course design for AI literacy concepts from the SAIL framework.`,
                llmId: selectedLlm,
            };
            await handleAddMessage(params);
        } catch (error) {
            // Error handled in handleAddMessage
        } finally {
            setInitialLoading(false);
        }
    };

    const sendMessage = async () => {
        if (!input.trim()) return;
        const tempId = generateTempId();
        const tempMessage: Message = {
            id: tempId,
            chatId: parseInt(chatId, 10),
            inputText: input,
            responseText: "",
            createdAt: new Date(),
        };

        setMessages((prev) => [...prev, tempMessage]);
        setLoadingMessages((prev) => [...prev, tempId]);

        try {
            const params = {
                inputText: input,
                llmId: selectedLlm,
            };
            await handleAddMessage(params, tempId);
        } catch (error) {
            // Error handled in handleAddMessage
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLlmSelect = (llmId: number) => {
        setSelectedLlm(llmId);
        handleMenuClose();
    };

    const messageFeed = () => {
        if (initialLoading && messages.length === 0) {
            return (
                <EvaluationRatingMessage
                    key={tempInitialMessage.id}
                    message={tempInitialMessage}
                    deleteMessage={handleDeleteMessage}
                    loading={true}
                    disableEditDelete={true}
                />
            );
        }
        if (!messages) return <>Loading...</>;
        return (
            <>
                {messages.map((message: Message, index: number) => {
                    return(
                        <EvaluationRatingMessage
                            key={message.id}
                            message={message}
                            deleteMessage={handleDeleteMessage}
                            loading={loadingMessages.includes(message.id)}
                            disableEditDelete={true}
                        />
                    );
                })}
            </>
        );
    };

    const llmMenu = () => {
        if (!llmList || llmList.length === 0) return <MenuItem disabled>No LLMs available</MenuItem>;
        return (
            <>
                {llmList.map((llm) => (
                    <MenuItem
                        key={llm.id}
                        onClick={() => handleLlmSelect(llm.id)}
                        selected={llm.id === selectedLlm}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {llm.icon}
                            <Box>
                                <Typography variant="body1">{llm.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {llm.description}
                                </Typography>
                            </Box>
                        </Box>
                    </MenuItem>
                ))}
            </>
        );
    };

    const getSelectedLlmName = () => {
        if (!llmList || llmList.length === 0) return "Select LLM";
        const selected = llmList.find((llm) => llm.id === selectedLlm);
        return selected ? selected.name : "Select LLM";
    };

    const handleFetchCourseChats = () => {
        fetchCourseChats(parseInt(courseId, 10))
            .then(() => {
            })
            .catch((error) => {
                showSnackbar('Failed to fetch course chats', 'error');
            });
    };

    const handleOpenChatDialog = () => {
        if(suggestionChatId === 0){
            setOpenChatDialog(true);
        }else {
            navigate(`/courses/${courseId}/chat/${suggestionChatId}`);
        }
    };

    const handleCloseChatDialog = () => {
        setOpenChatDialog(false);
    };

    const triggerToggle = () => {
        if (!loading) {
            setLoading(true);
            setTimeout(() => {
                setLoading(false);
            }, 10000);
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100vh", // Fits the viewport height, no outer scroll
            }}
        >
            <Container
                maxWidth="lg"
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    height: "100%",
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        overflowY: "auto", // Enables scrolling for title, instructions, and messages
                        maxHeight: "calc(100% - 100px)", // Leaves space for input bar
                        mt: 5,
                    }}
                >
                    <Typography variant="h4" gutterBottom sx={{ textAlign: "center", mb: 4 }}>
                        {`${course?.courseName || "Loading..."} AI Literacy Evaluation`}
                    </Typography>
                    <Typography variant="subtitle1" gutterBottom sx={{ textAlign: "center", mb: 4 }}>
                        {localization.evaluationChat.instructions}
                    </Typography>
                    {messageFeed()}
                    <Box ref={scrollRef} />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '120%', // Responsive to parent
                        maxWidth: 'lg', // Matches Material-UI's lg breakpoint
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)', // Centers Box relative to parent
                        bottom: 0, // Anchors to bottom of Container
                        zIndex: 1100, // Ensures Box is above other elements
                        pb: 3,
                    }}
                >
                    <Button
                        color="primary"
                        startIcon={<NavigateBeforeIcon />}
                        onClick={() => navigate(`/courses/${courseId}/form`)}
                    >
                        Course Info
                    </Button>
                    <Menu
                        id="llm-menu"
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                    >
                        {llmMenu()}
                    </Menu>
                    <Button
                        color="primary"
                        endIcon={<NavigateNextIcon />}
                        onClick={handleOpenChatDialog}
                    >
                        Suggestion Mode
                    </Button>
                </Box>
            </Container>
            {course && (
                <ChatDialog
                    course={course}
                    open={openChatDialog}
                    onClose={handleCloseChatDialog}
                    onConfirm={handleFetchCourseChats}
                />
            )}
        </Box>
    );

};

export default EvaluationPage;